import {
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  QUIET_CONTEXT_MENU_ITEM_CLASS,
  QUIET_CONTEXT_MENU_SHELL_CLASS,
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import {
  clearPanelDestinationPreview,
  setPanelDestinationPreview,
} from '@/lib/panel-destination-preview'
import {
  CLOSE_TRANSIENT_MENUS_EVENT,
  type CloseTransientMenusDetail,
} from '@/lib/transient-menus'

export type EntityContextMenuAction = {
  label: string
  icon?: LucideIcon | ReactNode
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
  separatorBefore?: boolean
  className?: string
  testId?: string
  /** Optional trailing hint (e.g. snooze "Today 7pm"). */
  hint?: string
  /** Highlight this layout region while the menu item is hovered or keyboard-focused. */
  previewPanel?: 'main' | 'agent'
}

export type EntityContextMenuSubmenu = {
  label: string
  icon?: LucideIcon | ReactNode
  disabled?: boolean
  separatorBefore?: boolean
  children: Array<EntityContextMenuAction | { separator: true }>
}

export type EntityContextMenuEntry =
  | EntityContextMenuAction
  | EntityContextMenuSubmenu
  | { separator: true }
  | { custom: ReactNode }

export type EntityContextMenuProps = {
  x: number
  y: number
  onClose: () => void
  entries: EntityContextMenuEntry[]
  /**
   * When set, ignore exclusive-close events from this source so a row can
   * dismiss other menus then open this one in the same gesture.
   */
  menuSourceId?: string
  'data-testid'?: string
}

function isCustomEntry(entry: EntityContextMenuEntry): entry is { custom: ReactNode } {
  return 'custom' in entry
}

function isSeparatorEntry(entry: EntityContextMenuEntry): entry is { separator: true } {
  return 'separator' in entry && entry.separator === true
}

function isSubmenuEntry(entry: EntityContextMenuEntry): entry is EntityContextMenuSubmenu {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'children' in entry &&
    Array.isArray((entry as EntityContextMenuSubmenu).children)
  )
}

function isActionEntry(entry: EntityContextMenuEntry): entry is EntityContextMenuAction {
  return !isSeparatorEntry(entry) && !isCustomEntry(entry) && !isSubmenuEntry(entry)
}

function renderIcon(icon: EntityContextMenuAction['icon']) {
  if (!icon) return null
  if (isValidElement(icon)) return icon
  // Lucide icons are function components or forwardRef objects ({ render })
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null && 'render' in icon)) {
    const Icon = icon as LucideIcon
    return <Icon className="h-4 w-4" />
  }
  return icon
}

/**
 * Shared right-click menu shell: fixed position, role="menu", keyboard navigable.
 * Per-surface menus pass action descriptors; do not hand-roll popover markup.
 */
export function EntityContextMenu({
  x,
  y,
  onClose,
  entries,
  menuSourceId,
  'data-testid': testId,
}: EntityContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const submenuPortalRef = useRef<HTMLDivElement>(null)
  const subTriggerRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const [adjustedPosition, setAdjustedPosition] = useState({ x, y })
  const [focusIndex, setFocusIndex] = useState(0)
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null)
  const [subFocusIndex, setSubFocusIndex] = useState(0)
  const [submenuPos, setSubmenuPos] = useState({ x: 0, y: 0 })
  const closeSubTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const focusableIndexes = entries
    .map((entry, index) => {
      if (isActionEntry(entry) && !entry.disabled) return index
      if (isSubmenuEntry(entry) && !entry.disabled) return index
      return -1
    })
    .filter((index) => index >= 0)

  const handleClose = useCallback(() => {
    clearPanelDestinationPreview()
    setOpenSubmenuIndex(null)
    onClose()
  }, [onClose])

  const clearCloseSubTimer = useCallback(() => {
    if (closeSubTimer.current) {
      clearTimeout(closeSubTimer.current)
      closeSubTimer.current = null
    }
  }, [])

  const scheduleCloseSubmenu = useCallback(() => {
    clearCloseSubTimer()
    closeSubTimer.current = setTimeout(() => {
      setOpenSubmenuIndex(null)
      closeSubTimer.current = null
    }, 150)
  }, [clearCloseSubTimer])

  const openSubmenu = useCallback(
    (index: number) => {
      clearCloseSubTimer()
      setOpenSubmenuIndex(index)
      setSubFocusIndex(0)
    },
    [clearCloseSubTimer],
  )

  const applyPreviewForEntryIndex = useCallback(
    (index: number) => {
      const entry = entries[index]
      if (isActionEntry(entry) && entry.previewPanel) {
        setPanelDestinationPreview(entry.previewPanel)
      } else {
        clearPanelDestinationPreview()
      }
    },
    [entries],
  )

  useLayoutEffect(() => {
    if (!menuRef.current) {
      setAdjustedPosition({ x, y })
      return
    }

    const menuRect = menuRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 8

    let adjustedX = x
    let adjustedY = y

    if (x + menuRect.width > viewportWidth - padding) {
      adjustedX = Math.max(padding, viewportWidth - menuRect.width - padding)
    }
    if (adjustedX < padding) adjustedX = padding

    if (y + menuRect.height > viewportHeight - padding) {
      adjustedY = Math.max(padding, viewportHeight - menuRect.height - padding)
    }
    if (adjustedY < padding) adjustedY = padding

    setAdjustedPosition({ x: adjustedX, y: adjustedY })
  }, [x, y, entries.length, openSubmenuIndex])

  useLayoutEffect(() => {
    if (openSubmenuIndex === null) return
    const trigger = subTriggerRefs.current.get(openSubmenuIndex)
    if (!trigger) return
    const t = trigger.getBoundingClientRect()
    const padding = 8
    const panel = submenuPortalRef.current
    const panelW = panel?.offsetWidth ?? 176
    const panelH = panel?.offsetHeight ?? 0
    let nextX = t.right + 2
    let nextY = t.top
    if (nextX + panelW > window.innerWidth - padding) {
      nextX = Math.max(padding, t.left - panelW - 2)
    }
    if (panelH > 0 && nextY + panelH > window.innerHeight - padding) {
      nextY = Math.max(padding, window.innerHeight - panelH - padding)
    }
    setSubmenuPos((prev) => (prev.x === nextX && prev.y === nextY ? prev : { x: nextX, y: nextY }))
  }, [openSubmenuIndex, entries.length])

  useEffect(() => {
    const openedAt = performance.now()
    const handleClickOutside = (event: MouseEvent) => {
      // The right-click that opened this menu can deliver a trailing mousedown.
      // Ignore that gesture so the menu is not dismissed in the same click.
      if (event.button !== 0) return
      if (performance.now() - openedAt < 300) return
      const target = event.target as Node
      const inMenu = !!menuRef.current?.contains(target)
      const inSub = !!submenuPortalRef.current?.contains(target)
      if (inMenu || inSub) return
      handleClose()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        if (openSubmenuIndex !== null) {
          setOpenSubmenuIndex(null)
          return
        }
        handleClose()
        return
      }

      const openSub = openSubmenuIndex !== null ? entries[openSubmenuIndex] : null
      if (openSub && isSubmenuEntry(openSub)) {
        const subActions = openSub.children
          .map((child, i) => (isActionEntry(child) && !child.disabled ? i : -1))
          .filter((i) => i >= 0)

        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          setOpenSubmenuIndex(null)
          return
        }

        if (subActions.length > 0) {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setSubFocusIndex((current) => {
              const pos = subActions.indexOf(current)
              const nextPos = pos < 0 ? 0 : (pos + 1) % subActions.length
              return subActions[nextPos]
            })
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setSubFocusIndex((current) => {
              const pos = subActions.indexOf(current)
              const nextPos =
                pos <= 0 ? subActions[subActions.length - 1] : subActions[pos - 1]
              return subActions[nextPos]
            })
            return
          }
          if (event.key === 'Enter') {
            const child = openSub.children[subFocusIndex]
            if (isActionEntry(child) && !child.disabled) {
              event.preventDefault()
              child.onSelect()
              handleClose()
            }
            return
          }
        }
      }

      if (focusableIndexes.length === 0) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setOpenSubmenuIndex(null)
        setFocusIndex((current) => {
          const pos = focusableIndexes.indexOf(current)
          const nextPos = pos < 0 ? 0 : (pos + 1) % focusableIndexes.length
          const nextIndex = focusableIndexes[nextPos]
          applyPreviewForEntryIndex(nextIndex)
          return nextIndex
        })
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setOpenSubmenuIndex(null)
        setFocusIndex((current) => {
          const pos = focusableIndexes.indexOf(current)
          const nextPos =
            pos <= 0
              ? focusableIndexes[focusableIndexes.length - 1]
              : focusableIndexes[pos - 1]
          const nextIndex = focusableIndexes[nextPos]
          applyPreviewForEntryIndex(nextIndex)
          return nextIndex
        })
      }

      if (event.key === 'ArrowRight') {
        const entry = entries[focusIndex]
        if (isSubmenuEntry(entry) && !entry.disabled) {
          event.preventDefault()
          openSubmenu(focusIndex)
        }
      }

      if (event.key === 'Enter') {
        const entry = entries[focusIndex]
        if (isSubmenuEntry(entry) && !entry.disabled) {
          event.preventDefault()
          openSubmenu(focusIndex)
          return
        }
        if (isActionEntry(entry) && !entry.disabled) {
          event.preventDefault()
          entry.onSelect()
          handleClose()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    applyPreviewForEntryIndex,
    entries,
    focusIndex,
    focusableIndexes,
    handleClose,
    openSubmenu,
    openSubmenuIndex,
    subFocusIndex,
  ])

  useEffect(() => {
    const onExclusiveClose = (event: Event) => {
      const detail = (event as CustomEvent<CloseTransientMenusDetail>).detail
      if (menuSourceId && detail?.source === menuSourceId) return
      handleClose()
    }
    window.addEventListener(CLOSE_TRANSIENT_MENUS_EVENT, onExclusiveClose)
    return () => {
      window.removeEventListener(CLOSE_TRANSIENT_MENUS_EVENT, onExclusiveClose)
    }
  }, [handleClose, menuSourceId])

  useEffect(() => {
    return () => {
      clearPanelDestinationPreview()
      clearCloseSubTimer()
    }
  }, [clearCloseSubTimer])

  useEffect(() => {
    if (focusableIndexes.length > 0) {
      setFocusIndex(focusableIndexes[0])
    }
  }, [focusableIndexes.join(',')])

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      data-testid={testId}
      className={QUIET_CONTEXT_MENU_SHELL_CLASS}
      style={{ left: adjustedPosition.x, top: adjustedPosition.y, zIndex: 200 }}
      onMouseLeave={(event) => {
        const related = event.relatedTarget as Node | null
        if (related && submenuPortalRef.current?.contains(related)) return
        clearPanelDestinationPreview()
        scheduleCloseSubmenu()
      }}
    >
      {entries.map((entry, index) => {
        if (isSeparatorEntry(entry)) {
          return <div key={`sep-${index}`} className="h-px bg-border/60 my-0.5" role="separator" />
        }

        if (isCustomEntry(entry)) {
          return <div key={`custom-${index}`}>{entry.custom}</div>
        }

        if (isSubmenuEntry(entry)) {
          const subOpen = openSubmenuIndex === index
          const submenuPanel = subOpen
            ? createPortal(
                <div
                  ref={submenuPortalRef}
                  role="menu"
                  data-testid="entity-context-submenu"
                  className={cn(
                    QUIET_OVERFLOW_MENU_CONTENT_CLASS,
                    'fixed z-[100] min-w-[11rem] rounded-md border border-border/60 bg-popover shadow-sm',
                  )}
                  style={{ left: submenuPos.x, top: submenuPos.y }}
                  onMouseEnter={clearCloseSubTimer}
                  onMouseLeave={(event) => {
                    const related = event.relatedTarget as Node | null
                    if (related && menuRef.current?.contains(related)) {
                      clearCloseSubTimer()
                      return
                    }
                    scheduleCloseSubmenu()
                  }}
                >
                  {entry.children.map((child, childIndex) => {
                    if (isSeparatorEntry(child)) {
                      return (
                        <div
                          key={`sub-sep-${childIndex}`}
                          className="h-px bg-border/60 my-0.5"
                          role="separator"
                        />
                      )
                    }
                    return (
                      <button
                        key={`${child.label}-${childIndex}`}
                        type="button"
                        role="menuitem"
                        disabled={child.disabled}
                        tabIndex={subFocusIndex === childIndex ? 0 : -1}
                        onClick={() => {
                          if (child.disabled) return
                          child.onSelect()
                          handleClose()
                        }}
                        className={cn(
                          QUIET_CONTEXT_MENU_ITEM_CLASS,
                          'justify-between gap-4',
                          child.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                          child.destructive && QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
                          child.className,
                        )}
                      >
                        <span className="flex items-center gap-2.5 min-w-0">
                          <span className="flex-shrink-0">{renderIcon(child.icon)}</span>
                          <span>{child.label}</span>
                        </span>
                        {child.hint ? (
                          <span className="tabular-nums text-xs text-muted-foreground shrink-0">
                            {child.hint}
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>,
                document.body,
              )
            : null
          return (
            <div
              key={`sub-${entry.label}-${index}`}
              className="relative"
              onMouseEnter={() => {
                if (!entry.disabled) openSubmenu(index)
              }}
              onMouseLeave={(event) => {
                const related = event.relatedTarget as Node | null
                if (related && submenuPortalRef.current?.contains(related)) return
                scheduleCloseSubmenu()
              }}
            >
              {entry.separatorBefore ? (
                <div className="h-px bg-border/60 my-0.5" role="separator" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={subOpen}
                disabled={entry.disabled}
                tabIndex={focusIndex === index ? 0 : -1}
                ref={(node) => {
                  if (node) subTriggerRefs.current.set(index, node)
                  else subTriggerRefs.current.delete(index)
                }}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  if (entry.disabled) return
                  openSubmenu(index)
                }}
                className={cn(
                  QUIET_CONTEXT_MENU_ITEM_CLASS,
                  'justify-between',
                  entry.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                  subOpen && 'bg-accent text-accent-foreground',
                )}
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="flex-shrink-0">{renderIcon(entry.icon)}</span>
                  <span>{entry.label}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
              {submenuPanel}
            </div>
          )
        }

        const item = entry
        return (
          <div key={`${item.label}-${index}`}>
            {item.separatorBefore ? (
              <div className="h-px bg-border/60 my-0.5" role="separator" />
            ) : null}
            <button
              type="button"
              role="menuitem"
              data-testid={item.testId}
              disabled={item.disabled}
              tabIndex={focusIndex === index ? 0 : -1}
              onMouseEnter={() => {
                setOpenSubmenuIndex(null)
                applyPreviewForEntryIndex(index)
              }}
              onClick={() => {
                if (item.disabled) return
                item.onSelect()
                handleClose()
              }}
              className={cn(
                QUIET_CONTEXT_MENU_ITEM_CLASS,
                item.hint && 'justify-between gap-4',
                item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
                item.destructive && QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
                item.className,
              )}
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span className="flex-shrink-0">{renderIcon(item.icon)}</span>
                <span>{item.label}</span>
              </span>
              {item.hint ? (
                <span className="tabular-nums text-xs text-muted-foreground shrink-0">
                  {item.hint}
                </span>
              ) : null}
            </button>
          </div>
        )
      })}
    </div>
  )

  if (typeof document === 'undefined') return menu
  return createPortal(menu, document.body)
}
